/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Switch, FormControlLabel } from '@mui/material';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { StepContainer, StepTitle, FormGroup, ToggleContainer, InfoBox } from './styles';
import axios from 'axios';
import { BASE_API, USER_TOKEN } from 'config/consts';

// Mapeamento dos principais códigos de banco para nomes
const bankNames = {
  '001': 'Banco do Brasil',
  '033': 'Santander',
  '104': 'Caixa Econômica',
  '237': 'Bradesco',
  '341': 'Itaú',
  '077': 'Inter',
  '260': 'Nubank',
  '290': 'PagBank',
  '336': 'C6 Bank',
  '212': 'Banco Original',
  '756': 'Sicoob',
  '748': 'Sicredi',
  '422': 'Safra',
  '070': 'BRB',
  '655': 'Neon',
  '323': 'Mercado Pago',
  '380': 'PicPay',
  '403': 'Cora',
};

const getBankName = (bankCode) => {
  return bankNames[bankCode] || `Banco ${bankCode}`;
};

const renderTextField = ({ input, label, meta: { touched, error }, ...custom }) => (
  <TextField
    label={label}
    variant="outlined"
    error={touched && Boolean(error)}
    helperText={touched && error}
    {...input}
    {...custom}
    fullWidth
  />
);

const renderSelectField = ({ input, label, meta: { touched, error }, children, ...custom }) => (
  <TextField
    select
    label={label}
    variant="outlined"
    error={touched && Boolean(error)}
    helperText={touched && error}
    {...input}
    {...custom}
    fullWidth
  >
    {children}
  </TextField>
);

const getValidPaymentLinkAccounts = (accounts = []) => {
  return (Array.isArray(accounts) ? accounts : []).filter(
    (account) =>
      account?.external_id &&
      account?.bank_code &&
      account?.agency &&
      account?.account_number
  );
};

const DadosParceiro = ({ formValues, change, stationId, stationIds = [] }) => {
  const [hasPartner, setHasPartner] = useState(formValues?.has_partner || false);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Buscar lista de parceiros com contas de payment_link
    const fetchPartners = async () => {
      const stationIdsToFetch = Array.from(new Set([...(stationIds || []), stationId].filter(Boolean)));

      console.log('DadosParceiro - stationIds:', stationIdsToFetch);
      if (stationIdsToFetch.length === 0) {
        console.log('DadosParceiro - stationIds não definidos');
        setPartners([]);
        return;
      }
      
      setLoading(true);
      try {
        const token = JSON.parse(localStorage.getItem(USER_TOKEN));
        const responses = await Promise.all(
          stationIdsToFetch.map((currentStationId) =>
            axios
              .get(`${BASE_API}/gateway-digital-accounts/payment-link-partners/${currentStationId}`, {
                headers: {
                  Authorization: `${token}`,
                },
              })
              .catch((error) => {
                console.error(`Erro ao buscar parceiros da station ${currentStationId}:`, error);
                return { data: [] };
              })
          )
        );

        const mergedPartners = [];
        const partnerIndexMap = new Map();

        responses.forEach((response) => {
          const currentPartners = Array.isArray(response.data) ? response.data : [];

          currentPartners.forEach((partner) => {
            if (!partner?.user_id || !partner?.establishment_id) {
              return;
            }

            const partnerKey = `${partner.user_id}-${partner.establishment_id}`;
            const existingIndex = partnerIndexMap.get(partnerKey);

            if (existingIndex === undefined) {
              const validAccounts = getValidPaymentLinkAccounts(partner.accounts);

              if (validAccounts.length === 0) {
                return;
              }

              mergedPartners.push({
                ...partner,
                accounts: validAccounts,
              });
              partnerIndexMap.set(partnerKey, mergedPartners.length - 1);
              return;
            }

            const existingPartner = mergedPartners[existingIndex];
            const existingAccounts = Array.isArray(existingPartner.accounts)
              ? existingPartner.accounts
              : [];
            const incomingAccounts = getValidPaymentLinkAccounts(partner.accounts);
            const accountsMap = new Map(
              existingAccounts.map((account) => [account.local_id || account.external_id, account])
            );

            incomingAccounts.forEach((account) => {
              const accountKey = account.local_id || account.external_id;
              if (!accountsMap.has(accountKey)) {
                accountsMap.set(accountKey, account);
              }
            });

            mergedPartners[existingIndex] = {
              ...existingPartner,
              ...partner,
              accounts: Array.from(accountsMap.values()),
            };
          });
        });

        console.log('DadosParceiro - Parceiros encontrados:', mergedPartners);
        setPartners(mergedPartners);
      } catch (error) {
        console.error('Erro ao buscar parceiros:', error);
        setPartners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, [stationId, stationIds]);

  useEffect(() => {
    if (formValues?.partner_user_id && formValues?.partner_establishment_id && partners.length > 0) {
      const partner = partners.find(
        p => p.user_id === formValues.partner_user_id && p.establishment_id === formValues.partner_establishment_id
      );
      setSelectedPartner(partner);
      
      if (formValues?.partner_account_id && partner?.accounts) {
        const account = partner.accounts.find(a => a.local_id === formValues.partner_account_id);
        setSelectedAccount(account);
      }
    }
  }, [formValues?.partner_user_id, formValues?.partner_establishment_id, formValues?.partner_account_id, partners]);

  const handleTogglePartner = (event) => {
    const value = event.target.checked;
    setHasPartner(value);
    change('has_partner', value);
    
    if (!value) {
      // Limpar campos do parceiro
      change('partner_user_id', null);
      change('partner_establishment_id', null);
      change('partner_name', '');
      change('partner_establishment_name', '');
      change('partner_cnpj', '');
      change('partner_email', '');
      change('partner_account_id', null);
      change('partner_recipient_id', '');
      change('partner_bank', '');
      change('partner_account', '');
      change('partner_account_summary', '');
      setSelectedPartner(null);
      setSelectedAccount(null);
    }
  };

  const handlePartnerChange = (event) => {
    const visitorId = event.target.value;
    // O value agora é a combinação user_id-establishment_id
    const partner = partners.find(p => `${p.user_id}-${p.establishment_id}` === visitorId);
    
    if (partner) {
      setSelectedPartner(partner);
      change('partner_user_id', partner.user_id);
      change('partner_establishment_id', partner.establishment_id);
      change('partner_name', partner.user_name);
      change('partner_establishment_name', partner.establishment_name);
      change('partner_cnpj', partner.cnpj);
      change('partner_email', partner.user_email || '');
      
      // Se o parceiro tem apenas uma conta, seleciona automaticamente
      if (partner.accounts && partner.accounts.length === 1) {
        const account = partner.accounts[0];
        setSelectedAccount(account);
        change('partner_account_id', account.local_id);
        change('partner_recipient_id', account.external_id);
        change('partner_bank', getBankName(account.bank_code));
        change('partner_account', `Ag: ${account.agency || '-'} | Cc: ${account.account_number || '-'}-${account.account_dv || '-'}`);
        change(
          'partner_account_summary',
          `${getBankName(account.bank_code)} | Ag: ${account.agency || '-'} | Cc: ${account.account_number || '-'}-${account.account_dv || '-'}`
        );
      } else {
        // Limpa a conta selecionada se houver múltiplas
        setSelectedAccount(null);
        change('partner_account_id', null);
        change('partner_recipient_id', '');
        change('partner_bank', '');
        change('partner_account', '');
        change('partner_account_summary', '');
      }
    }
  };

  const handleAccountChange = (event) => {
    const accountId = event.target.value;
    if (selectedPartner?.accounts) {
      const account = selectedPartner.accounts.find(a => a.local_id === parseInt(accountId));
      if (account) {
        setSelectedAccount(account);
        change('partner_account_id', account.local_id);
        change('partner_recipient_id', account.external_id);
        change('partner_bank', getBankName(account.bank_code));
        change('partner_account', `Ag: ${account.agency || '-'} | Cc: ${account.account_number || '-'}-${account.account_dv || '-'}`);
        change(
          'partner_account_summary',
          `${getBankName(account.bank_code)} | Ag: ${account.agency || '-'} | Cc: ${account.account_number || '-'}-${account.account_dv || '-'}`
        );
      }
    }
  };

  return (
    <StepContainer>
      <StepTitle>Dados do Parceiro</StepTitle>
      
      <ToggleContainer>
        <FormControlLabel
          control={
            <Switch
              checked={hasPartner}
              onChange={handleTogglePartner}
              color="primary"
            />
          }
          label="A atividade é relacionada com parceiro?"
        />
      </ToggleContainer>

      {hasPartner && (
        <>
          <InfoBox>
            Selecione um parceiro que tenha conta bancária cadastrada para Link de Pagamento. 
            {partners.length === 0 && !loading && (
              <strong> Nenhum parceiro com conta cadastrada foi encontrado.</strong>
            )}
          </InfoBox>

          <FormGroup>
            <Field
              name="partner_id"
              component={renderSelectField}
              label="Parceiro *"
              fullWidth
              required
              onChange={handlePartnerChange}
              disabled={loading}
            >
              <MenuItem value="">
                {loading ? 'Carregando parceiros...' : 'Selecione um parceiro'}
              </MenuItem>
              {partners.map(partner => (
                <MenuItem key={`${partner.user_id}-${partner.establishment_id}`} value={`${partner.user_id}-${partner.establishment_id}`}>
                  {partner.user_name} - {partner.establishment_name} {partner.accounts?.length > 1 ? `(${partner.accounts.length} contas)` : ''}
                </MenuItem>
              ))}
            </Field>
          </FormGroup>

          {selectedPartner && (
            <>
              <FormGroup>
                <Field
                  name="partner_name"
                  component={renderTextField}
                  label="Nome do Parceiro"
                  fullWidth
                  disabled
                />
              </FormGroup>

              <FormGroup>
                <Field
                  name="partner_establishment_name"
                  component={renderTextField}
                  label="Empresa/Estabelecimento"
                  fullWidth
                  disabled
                />
              </FormGroup>

              <FormGroup>
                <Field
                  name="partner_cnpj"
                  component={renderTextField}
                  label="CNPJ"
                  fullWidth
                  disabled
                />
              </FormGroup>

              <FormGroup>
                <Field
                  name="partner_email"
                  component={renderTextField}
                  label="E-mail"
                  fullWidth
                  disabled
                />
              </FormGroup>

              {/* Se o parceiro tem múltiplas contas, permite selecionar */}
              {selectedPartner.accounts && selectedPartner.accounts.length > 1 && (
                <FormGroup>
                  <Field
                    name="partner_account_id"
                    component={renderSelectField}
                    label="Conta bancária do parceiro *"
                    fullWidth
                    required
                    onChange={handleAccountChange}
                  >
                    <MenuItem value="">Selecione uma conta</MenuItem>
                    {selectedPartner.accounts.map(account => (
                      <MenuItem key={account.local_id} value={account.local_id}>
                        {getBankName(account.bank_code)} | Ag: {account.agency} | Cc: {account.account_number}-{account.account_dv}
                      </MenuItem>
                    ))}
                  </Field>
                </FormGroup>
              )}

              {/* Mostra dados da conta selecionada */}
              {selectedAccount && (
                <InfoBox style={{ backgroundColor: '#e8f5e9', borderColor: '#4caf50' }}>
                  <strong>Conta selecionada:</strong><br />
                  Titular: {selectedAccount.account_name || '-'}<br />
                  Banco: {getBankName(selectedAccount.bank_code)} | 
                  Agência: {selectedAccount.agency || '-'} | 
                  Conta: {selectedAccount.account_number || '-'}-{selectedAccount.account_dv || '-'}
                </InfoBox>
              )}
            </>
          )}
        </>
      )}
    </StepContainer>
  );
};

DadosParceiro.propTypes = {
  formValues: PropTypes.object,
  change: PropTypes.func.isRequired,
  stationId: PropTypes.number,
  stationIds: PropTypes.arrayOf(PropTypes.number),
};

export default DadosParceiro;
